import React from 'react';
import { Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { IntelligenceRisk } from '../../../types/intelligence';
import { amberColors } from '../../../utils/intelligenceTheme';

interface RiskTagProps {
  risk: IntelligenceRisk;
}

export const RiskTag: React.FC<RiskTagProps> = React.memo(({ risk }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { text, severity } = risk;

  const getSeverityStyle = (severity: 'low' | 'medium' | 'high') => {
    if (isDark) {
      // In dark mode, use amber palette for all risk levels (matching example design)
      return {
        backgroundColor: `${amberColors[500]}1A`, // 10% opacity
        borderColor: `${amberColors[600]}66`, // 40% opacity
        color: amberColors[300],
        dotColor: amberColors[400],
      };
    }

    // Light mode: use theme colors with severity variations
    switch (severity) {
      case 'high':
        return {
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          borderColor: alpha(theme.palette.error.main, 0.4),
          color: theme.palette.error.main,
          dotColor: theme.palette.error.main,
        };
      case 'medium':
        return {
          backgroundColor: alpha(theme.palette.warning.main, 0.1),
          borderColor: alpha(theme.palette.warning.main, 0.4),
          color: theme.palette.warning.main,
          dotColor: theme.palette.warning.main,
        };
      case 'low':
      default:
        return {
          backgroundColor: alpha(theme.palette.info.main, 0.1),
          borderColor: alpha(theme.palette.info.main, 0.4),
          color: theme.palette.info.main,
          dotColor: theme.palette.info.main,
        };
    }
  };

  const severityStyle = getSeverityStyle(severity);

  return (
    <Chip
      label={
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: severityStyle.dotColor,
              display: 'inline-block',
            }}
          />
          {text}
        </span>
      }
      size="small"
      sx={{
        backgroundColor: severityStyle.backgroundColor,
        border: `1px solid ${severityStyle.borderColor}`,
        color: severityStyle.color,
        fontSize: '0.75rem',
        fontWeight: 500,
        height: 28,
        '& .MuiChip-label': {
          px: 1.5,
          py: 0.5,
        },
      }}
    />
  );
});

export default RiskTag;